import React, { useState, useEffect } from 'react';
import { useUpgradeGate } from './useUpgradeGate';
import TechCard from './TechCard';
import ResearcherCard from './ResearcherCard';
import { SavedItemCard } from './SavedItemCard';
import { SavedItem, SavedCardType } from '../types';
import { ContactUniversityModal } from './modals/ContactUniversityModal';
import { PreviewMessageModal } from './modals/PreviewMessageModal';
import { ChatFeedback } from './ChatFeedback';
import { apiService } from '../services/apiService';
import { trackClick } from '../utils/analytics';
import { FloatingToast } from './InlineAlert';

interface SearchResult {
  score: number;
  content: string;
  metadata: {
    id: number;
    title?: string;
    keywords?: string;
    full_text?: string;
    utech_title?: string;
    common_email?: string;
    created_date: string;
    utech_summary?: string;
    ip_information?: string;
    utech_keywords?: string;
    university_name?: string;
    reference_number?: string;
    development_stage?: string;
    page_updated_date?: string;
    page_accessed_date?: string;
    researcher_details?: any[] | string; // Can be parsed array or JSON string
    university_country?: string;
    industry_categories?: string;
    utech_technology_id?: string;
    utech_university_id?: string;
    tech_manager_default?: any | string; // Can be parsed object or JSON string
    tech_manager_details?: any[] | string; // Can be parsed array or JSON string
    university_portal_url?: string;
    researcher_ids_details?: string;
    tech_manager_ids_details?: string;
    university_technology_url?: string;
    utech_industry_categories?: string;
    // Researcher-specific fields
    researcher_name?: string;
    utech_researcher_id?: string;
    // IP and Publications fields
    ip_summary?: string;
    publications_summary?: string;
    // TRL fields
    // OLD: interpreted_trl_reasoning?: string;
    calculated_trl?: number | null; // Numeric TRL value (new format)
    calculated_trl_statement?: string; // TRL reasoning statement
    // Confidence score for TRL
    confidence_score?: number | string;
  };
  algorithm: string;
  percentage: number;
  source_type: 'technology' | 'researcher';
}

interface BroadQueryMeta {
  total_results_count: number;
  returned_results_count: number;
  is_broad_query: boolean;
}

interface ChatSearchResultsProps {
  searchResults: SearchResult[];
  onTechCardClick?: (techId: string) => void;
  onContact?: () => void;
  // User profile data for contact modal
  userName?: string;
  personalSummary?: string;
  rdSummary?: string;
  // Chat session ID for linking saved items and feedback
  sessionId?: string;
  // AI message ID for feedback
  aiMessageId?: string;
  // Profile completion for contact restrictions
  profileIncomplete?: boolean;
  // Extensive search flag and handler
  extensiveSearch?: boolean;
  onRunExtensiveSearch?: () => void;
  /** Opens the concierge booking form; owned by App. */
  onTalkToConcierge?: () => void;
  // Handler to send a chat message programmatically (for narrow-down action)
  onSendMessage?: (text: string) => void;
  // Flag to indicate if this is a fresh message (not reloaded)
  isFreshMessage?: boolean;
  // Broad query metadata (total count, returned count)
  broadQueryMeta?: BroadQueryMeta;
}

export const ChatSearchResults: React.FC<ChatSearchResultsProps> = ({ 
  searchResults, 
  onTechCardClick = () => {},
  onContact = () => {},
  userName = '',
  personalSummary = '',
  rdSummary = '',
  sessionId = '',
  aiMessageId = '',
  profileIncomplete = false,
  extensiveSearch = false,
  onRunExtensiveSearch,
  onTalkToConcierge,
  onSendMessage,
  isFreshMessage = false,
  broadQueryMeta,
}) => {
  // Sort search results by relevance score (highest to lowest)
  const sortedSearchResults = React.useMemo(() => {
    return [...searchResults].sort((a, b) => {
      const scoreA = a.percentage || a.score * 100 || 0;
      const scoreB = b.percentage || b.score * 100 || 0;
      return scoreB - scoreA; // Highest to lowest
    });
  }, [searchResults]);
  const upgrade = useUpgradeGate();
  const [closedCards, setClosedCards] = useState<Set<number>>(new Set());
  const [modalOpen, setModalOpen] = useState<{type: 'tech' | 'researcher', data: any} | null>(null);
  const [bookmarkedCards, setBookmarkedCards] = useState<Set<string>>(new Set());
  const [likedCards, setLikedCards] = useState<Map<string, boolean | null>>(new Map()); // true=liked, false=disliked, null=no preference
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [currentContactData, setCurrentContactData] = useState<any>(null);
  const [previewData, setPreviewData] = useState<{message: string, summaryType: 'personal' | 'rd', techManagerName: string} | null>(null);
  const [isStateLoaded, setIsStateLoaded] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showAllResults, setShowAllResults] = useState(false);
  const [initialDisplayCount] = useState(5); // Show 5 results initially
  const [showExtensiveSearchPrompt, setShowExtensiveSearchPrompt] = useState(false); // Don't show by default
  const [hasInteractedWithPrompt, setHasInteractedWithPrompt] = useState(false); // Track if user has interacted with prompt

  // Reset show more state when search results change
  useEffect(() => {
    setShowAllResults(false);
    // Only show extensive search prompt for fresh messages that haven't been interacted with
    // Don't reset if user has already interacted with the prompt
    if (isFreshMessage && !hasInteractedWithPrompt && !extensiveSearch) {
      setShowExtensiveSearchPrompt(true);
    } else {
      setShowExtensiveSearchPrompt(false);
    }
  }, [searchResults, isFreshMessage, hasInteractedWithPrompt, extensiveSearch]);

  // Load saved states when component mounts or search results change
  useEffect(() => {
    const loadSavedStates = async () => {
      try {
        const userId = apiService.getStoredUserId();
        if (!userId || searchResults.length === 0) {
          setIsStateLoaded(true);
          return;
        }

        // Get all saved items for the user
        const savedResponse = await apiService.getSavedCards(userId, 'all');
        if (!savedResponse.success || !savedResponse.data?.items) {
          setIsStateLoaded(true);
          return;
        }

        const savedItems = savedResponse.data.items;
        const newBookmarkedCards = new Set<string>();
        const newLikedCards = new Map<string, boolean | null>();

        // Process each search result to check if it's saved/liked
        sortedSearchResults.forEach((result, index) => {
          // Create card ID based on the actual result type
          const isResearcher = result.source_type === 'researcher';
          const cardId = isResearcher ? `researcher-${index}` : `tech-${index}`;
          
          // Get the correct asset ID based on result type
          const assetId = isResearcher 
            ? (result.metadata.utech_researcher_id || result.metadata.id.toString())
            : (result.metadata.utech_technology_id || result.metadata.id.toString());
          
          const assetType = isResearcher ? 'researcher' : 'technology';
          
          // Check if this asset is in saved items
          const savedItem = savedItems.find((item: any) => 
            item.asset_type === assetType && item.asset_id === assetId
          );

          // Update bookmark states
          if (savedItem?.bookmarked) {
            newBookmarkedCards.add(cardId);
          }

          // Update like states
          if (savedItem && savedItem.liked !== undefined && savedItem.liked !== null) {
            newLikedCards.set(cardId, savedItem.liked);
          }
        });

        setBookmarkedCards(newBookmarkedCards);
        setLikedCards(newLikedCards);
        setIsStateLoaded(true);
      } catch (error) {
        console.error('Failed to load saved states:', error);
        setIsStateLoaded(true);
      }
    };

    loadSavedStates();
  }, [searchResults]); // Reload when search results change

  const closeCard = (cardId: number) => {
    setClosedCards(prev => {
      const newSet = new Set(prev);
      newSet.add(cardId);
      return newSet;
    });
  };

  const handleBookmarkToggle = async (id: string) => {
    trackClick('search_click_bookmark', { id });
    try {
      const userId = apiService.getStoredUserId();
      if (!userId) return;

      const isCurrentlyBookmarked = bookmarkedCards.has(id);
      const [type, index] = id.split('-');
      const result = sortedSearchResults[parseInt(index)];
      
      // Get the correct asset ID based on type
      const assetId = type === 'tech' 
        ? (result.metadata.utech_technology_id || result.metadata.id.toString())
        : (result.metadata.utech_researcher_id || result.metadata.id.toString());

      // Debug logging for researcher bookmarking
      if (type === 'researcher') {
        console.log('🔖 Researcher bookmark debug:', {
          cardId: id,
          type: type,
          index: index,
          resultMetadata: result.metadata,
          utech_researcher_id: result.metadata.utech_researcher_id,
          assetId: assetId,
          isCurrentlyBookmarked: isCurrentlyBookmarked
        });
      }

      // Call the saved API endpoint
      console.log('🔖 Making API call to saveCard:', {
        asset_type: type === 'tech' ? 'technology' : 'researcher',
        asset_id: assetId,
        bookmarked: !isCurrentlyBookmarked,
        userId: userId
      });

      const response = await apiService.saveCard({
        asset_type: type === 'tech' ? 'technology' : 'researcher',
        asset_id: assetId,
        bookmarked: !isCurrentlyBookmarked,
        liked: null,
        linked_chat_id: sessionId || null
      }, userId);

      console.log('🔖 API response:', response);

      if (response.success) {
        setBookmarkedCards(prev => {
          const newSet = new Set(prev);
          if (isCurrentlyBookmarked) {
            newSet.delete(id);
          } else {
            newSet.add(id);
          }
          return newSet;
        });
      }
    } catch (error) {
      console.error('Failed to toggle bookmark:', error);
    }
  };

  const handleLikeDislike = async (id: string, liked: boolean | null) => {
    try {
      const userId = apiService.getStoredUserId();
      if (!userId) return;

      const [type, index] = id.split('-');
      const result = sortedSearchResults[parseInt(index)];
      
      // Get the correct asset ID based on type
      const assetId = type === 'tech' 
        ? (result.metadata.utech_technology_id || result.metadata.id.toString())
        : (result.metadata.utech_researcher_id || result.metadata.id.toString());

      // Debug logging for researcher like/dislike
      if (type === 'researcher') {
        console.log('👍👎 Researcher like/dislike debug:', {
          cardId: id,
          type: type,
          index: index,
          liked: liked,
          resultMetadata: result.metadata,
          utech_researcher_id: result.metadata.utech_researcher_id,
          assetId: assetId
        });
      }
      
      console.log('👍👎 Making API call to likeCard:', {
        asset_type: type === 'tech' ? 'technology' : 'researcher',
        asset_id: assetId,
        liked: liked,
        userId: userId
      });

      // Call the liked API endpoint
      const response = await apiService.likeCard({
        asset_type: type === 'tech' ? 'technology' : 'researcher',
        asset_id: assetId,
        liked: liked,
        linked_chat_id: sessionId || null
      }, userId);

      console.log('👍👎 API response:', response);

      if (response.success) {
        // Update local state
        setLikedCards(prev => {
          const newMap = new Map(prev);
          newMap.set(id, liked);
          return newMap;
        });
        console.log('Like status updated:', liked);
      }
    } catch (error) {
      console.error('Failed to update like status:', error);
    }
  };

  const handleContactUniversity = (techData: any) => {
    setCurrentContactData(techData);
    setContactModalOpen(true);
  };

  const handlePreviewMessage = (message: string, summaryType: 'personal' | 'rd', techManagerName: string) => {
    setPreviewData({message, summaryType, techManagerName});
    setContactModalOpen(false);
    setPreviewModalOpen(true);
  };

  const handleReadMore = (type: SavedCardType, data: any, index: number) => {
    trackClick('search_click_read_more', { type, title: data?.title || data?.utech_title });
    if (type === SavedCardType.Tech) {
      setModalOpen({type: 'tech', data});
    } else {
      setModalOpen({type: 'researcher', data});
    }
  };

  const closeModal = () => {
    setModalOpen(null);
  };

  const closeContactModal = () => {
    setContactModalOpen(false);
    setCurrentContactData(null);
  };

  const closePreviewModal = () => {
    setPreviewModalOpen(false);
    setPreviewData(null);
  };

  const handleTechCardClick = async (techId: string) => {
    try {
      console.log('🔍 Tech card clicked from researcher:', techId);
      
      // Fetch technology details
      const response = await apiService.getTechnologyDetails(techId);
      if (response.success && response.data) {
        // Extract IP and publication summaries from ip_information
        let ipSummary = 'No IP summary available';
        let publicationsSummary = 'No publications summary available';
        
        if (response.data.ip_information) {
          try {
            const ipInfo = typeof response.data.ip_information === 'string' 
              ? JSON.parse(response.data.ip_information)
              : response.data.ip_information;
            ipSummary = ipInfo.ip_summary || ipSummary;
            publicationsSummary = ipInfo.publications_summary || publicationsSummary;
          } catch (e) {
            console.warn('Could not parse ip_information:', e);
          }
        }
        
        // Parse researchers from response data
        let researchers = [];
        try {
          if (response.data.researchers && Array.isArray(response.data.researchers)) {
            researchers = response.data.researchers;
          } else if (response.data.researcher_details) {
            const researcherDetails = typeof response.data.researcher_details === 'string'
              ? JSON.parse(response.data.researcher_details)
              : response.data.researcher_details;
            researchers = Array.isArray(researcherDetails) ? researcherDetails : [];
          }
        } catch (e) {
          console.warn('Could not parse researcher details:', e);
          researchers = [{ name: 'Unknown Researcher' }];
        }
        
        // Get the original researcher card relevance score to maintain consistency
        const originalResearcher = sortedSearchResults.find(result => 
          result.source_type === 'researcher' && 
          result.metadata && 
          (result.metadata.researcher_details?.includes(techId) || 
           (result.metadata.utech_technology_id && result.metadata.utech_technology_id === techId))
        );
        const researcherRelevance = originalResearcher ? Math.round(originalResearcher.percentage) : 100;
        
        // Check if this technology is already saved/liked
        let isBookmarked = false;
        let likedStatus = null;
        try {
          const userId = apiService.getStoredUserId();
          if (userId) {
            const savedResponse = await apiService.getSavedCards(userId, 'technology');
            if (savedResponse.success && savedResponse.data.items) {
              const savedTech = savedResponse.data.items.find((item: any) => item.asset_id === techId);
              if (savedTech) {
                isBookmarked = savedTech.bookmarked || false;
                likedStatus = savedTech.liked;
              }
            }
          }
        } catch (e) {
          console.warn('Could not load saved status for tech card:', e);
        }
        
        // Prepare tech card data with researcher's relevance score
        // Handle TRL display - use "TRL Not Disclosed" if trl contains "not disclosed"
        const trlValue = response.data.trl || 'TRL Not Disclosed';
        const trlReasoningValue = trlValue.toLowerCase().includes('not disclosed')
          ? 'TRL not disclosed. Please contact the university for more details.'
          : response.data.trl_reasoning;
        
        const techCardData = {
          id: response.data.utech_technology_id || techId,
          utech_technology_id: response.data.utech_technology_id || techId,
          relevanceScore: researcherRelevance, // Use researcher's relevance score
          title: response.data.utech_title || response.data.title || techId,
          institution: response.data.university_name || '',
          location: response.data.university_country || '',
          trl: trlValue,
          trlReasoning: trlReasoningValue,
          researchers: researchers,
          summary: response.data.utech_summary || response.data.summary || 'Technology summary not available',
          ipSummary: ipSummary,
          publicationsSummary: publicationsSummary,
          activeTab: "Summary" as const,
          onContact: () => handleContactUniversity({
            techTitle: response.data.utech_title || response.data.title || techId,
            techId: response.data.utech_technology_id || techId, // Always use utech_technology_id for data fetching
            university: response.data.university_name || '', // for the Talk to Concierge lead
            referenceNumber: response.data.reference_number || '', // Separate reference number for display
            researcherName: researchers[0]?.name || 'Researcher',
            userName: userName,
            personalSummary: personalSummary,
            rdSummary: rdSummary
          }),
          onClose: closeModal,
          onBookmarkToggle: async (isBookmarked: boolean) => {
            try {
              const userId = apiService.getStoredUserId();
              if (userId && techId) {
                const response = await apiService.saveCard({
                  asset_type: 'technology',
                  asset_id: techId,
                  bookmarked: isBookmarked,
                  liked: null,
                  linked_chat_id: sessionId || null
                }, userId);
                
                if (response.success) {
                  console.log('Tech card bookmark updated:', isBookmarked);
                }
              }
            } catch (error) {
              console.error('Error toggling tech card bookmark:', error);
            }
          },
          onLikeDislike: async (liked: boolean | null) => {
            try {
              const userId = apiService.getStoredUserId();
              if (userId && techId) {
                const response = await apiService.likeCard({
                  asset_type: 'technology',
                  asset_id: techId,
                  liked: liked,
                  linked_chat_id: sessionId || null
                }, userId);
                
                if (response.success) {
                  console.log('Tech card like updated:', liked);
                }
              }
            } catch (error) {
              console.error('Error updating tech card like:', error);
            }
          },
          initialBookmarked: isBookmarked, // ✅ Load actual bookmark status
          initialLiked: likedStatus // ✅ Load actual like status
        };
        
        // Open tech card modal
        setModalOpen({type: 'tech', data: techCardData});
      } else {
        console.error('Failed to fetch technology details:', response.error);
      }
    } catch (error) {
      console.error('Error fetching technology details:', error);
    }
  };

  const handleSendEmail = async (emailData: {
    subject: string;
    content: string;
    techId: string;
    researcherName: string;
  }) => {
    try {
      const userId = apiService.getStoredUserId();
      if (!userId) {
        console.error('User not logged in');
        return;
      }

      // Generate a unique conversation ID
      const conversationId = `${emailData.techId}-${Date.now()}-${userId}`;
      
      // Format content as HTML
      const htmlContent = `
        <html>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
          <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
            <h2 style="color: #2c3e50; margin-bottom: 20px;">Technology Partnership Opportunity</h2>
            
            <p style="margin-bottom: 15px;">Dear ${emailData.researcherName},</p>
            
            <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
              ${emailData.content.replace(/\n\n/g, '</p><p style="margin-bottom: 15px;">').replace(/\n/g, '<br>')}
            </div>
            
            <p style="margin-top: 20px; font-size: 14px; color: #666;">
              This email was sent through <a href="https://universitytech.io" style="color: #007bff; text-decoration: none;">UniversityTech.io</a> 
              to facilitate technology transfer and collaboration opportunities.
            </p>
            
            <hr style="border: none; border-top: 1px solid #eee; margin: 20px 0;">
            
            <p style="font-size: 12px; color: #999; text-align: center;">
              This message was sent via UniversityTech.io's secure email system.
            </p>
          </div>
        </body>
        </html>
      `;
      
      // Fetch tech data to get tech manager info and common_email
      let techManagerEmails: string[] = [];
      let techManagerNames: string[] = [];
      let commonEmail: string | undefined = undefined;
      let ccEmail: string | undefined = undefined;
      
      // Set when the account is over its university-contact limit: the
      // contacts endpoint withholds the addresses, so there is no one to send
      // to (and routing it to ops would be wrong too).
      let contactBlockReason: string | null = null;

      try {
        // The metered contacts endpoint: the same four fields the details
        // endpoint used to carry, under `contacts`.
        const contactsResponse = await apiService.getTechnologyContacts(emailData.techId,
          { surface: 'send_email', purpose: 'send' });
        if (contactsResponse.success && contactsResponse.data && !contactsResponse.data.allowed) {
          contactBlockReason = contactsResponse.data.reason || 'limit_reached';
        }
        const techContacts: any = contactsResponse.success && contactsResponse.data?.allowed
          ? contactsResponse.data.contacts : null;
        if (techContacts) {
          // Get common_email for CC
          commonEmail = techContacts.common_email;
          
          // Curated tech-transfer office (technologies.tto_office_details), loaded
          // from the reviewed Global Universities sheet. NULL means not curated yet.
          // The details endpoint may hand JSON columns back as strings, so parse it
          // the same way tech_manager_default is treated elsewhere: object, JSON
          // string, or nothing. Its emails are always an array of clean addresses.
          const rawOffice = techContacts.tto_office_details;
          let ttoOffice: any = null;
          try {
            ttoOffice = typeof rawOffice === 'object' && rawOffice !== null
              ? rawOffice
              : (typeof rawOffice === 'string' && rawOffice.trim() ? JSON.parse(rawOffice) : null);
          } catch (e) {
            ttoOffice = null;
          }
          const ttoOfficeEmails: string[] = Array.isArray(ttoOffice?.emails)
            ? ttoOffice.emails.filter((e: any) => typeof e === 'string' && e.trim())
            : [];
          
          // CC: the curated office when we have one (every address; the backend
          // splits cc_email on ','/';' and drops whichever one becomes TO), else
          // the legacy common_email string as before.
          ccEmail = ttoOfficeEmails.length > 0 ? ttoOfficeEmails.join(', ') : commonEmail;
          
          // 1. Try tech_manager_details (list or dict)
          const techManagerDetails = techContacts.tech_manager_details;
          if (techManagerDetails) {
            const detailsList = Array.isArray(techManagerDetails) ? techManagerDetails : [techManagerDetails];
            detailsList.forEach((manager: any) => {
              if (manager?.email) {
                techManagerEmails.push(manager.email);
                if (manager.name) techManagerNames.push(manager.name);
              }
            });
          }
          
          // 2. Fallback: curated office first, then tech_manager_default.
          // The curated office is the reviewed contact; tech_manager_default is
          // scraped, and for 7 universities its "name" is the university's own
          // name rather than an office (WARF's is a person), so it only comes
          // into play when nothing curated exists.
          if (techManagerEmails.length === 0 && ttoOfficeEmails.length > 0) {
            techManagerEmails.push(ttoOfficeEmails[0]);
            if (ttoOffice?.name) techManagerNames.push(ttoOffice.name);
          }
          if (techManagerEmails.length === 0) {
            const techManagerDefault = techContacts.tech_manager_default;
            if (techManagerDefault?.email) {
              techManagerEmails.push(techManagerDefault.email);
              if (techManagerDefault.name) techManagerNames.push(techManagerDefault.name);
            }
          }
        }
      } catch (error) {
        console.warn('Could not fetch tech manager info:', error);
      }

      if (contactBlockReason) {
        setErrorMessage(contactBlockReason === 'upgrade_required'
          ? "University contacts are part of our trial and paid plans, so this message wasn't sent."
          : "You've reached your limit for university contacts, so this message wasn't sent. "
            + 'You can request more from the Contact University window.');
        setTimeout(() => setErrorMessage(''), 8000);
        return;
      }
      
      // 3. Final fallback — route to ops, never to a personal address.
      // We used to ship to a hardcoded dev email here, which caused a
      // real lead to be misrouted on 2026-05-13. See
      // Backend/DOCS/BUG_2026-05-13_misrouted_tto_email.md.
      const universityEmail = techManagerEmails.length > 0
        ? techManagerEmails[0]
        : (commonEmail || 'info@universitytech.io');
      
      const allTechManagerEmails = techManagerEmails.length > 0 
        ? techManagerEmails.join(', ') 
        : 'Not available in data';
      const techManagerName = techManagerNames.length > 0 
        ? techManagerNames.join(', ') 
        : 'Unknown';
      
      console.log('📧 Sending email to university:', universityEmail);
      console.log('   Tech ID:', emailData.techId);
      console.log('   Subject:', emailData.subject);
      console.log('   Researcher Name:', emailData.researcherName);
      console.log('   User ID:', userId);
      console.log('   🏛️ Actual Tech Manager Name(s):', techManagerName);
      console.log('   📧 All Tech Manager Email(s):', allTechManagerEmails);
      console.log('   📧 Primary Email (TO):', universityEmail);
      console.log('   📧 CC:', ccEmail || 'Not available');
      
      const emailPayload = {
        to_email: universityEmail, // universityEmail variable - change above to switch recipients
        subject: emailData.subject,
        content: htmlContent,
        conversation_id: conversationId,
        user_id: userId,
        tracking_consent: true,
        is_reply: false,
        utech_technology_id: emailData.techId, // persisted on email_conversations
        ...(ccEmail && { cc_email: ccEmail }) // curated office emails, else common_email
      };

      const response = await apiService.sendTrackedEmail(emailPayload);
      
      if (response.success) {
        console.log('Email sent successfully:', response.data);
        // Close modals and show success message
        setPreviewModalOpen(false);
        setCurrentContactData(null);
        
        // Show success notification
        setSuccessMessage('Email sent successfully! You can track the conversation in your Inbox.');
        setTimeout(() => setSuccessMessage(''), 5000);
        
        // Trigger inbox refresh by setting a flag in localStorage
        localStorage.setItem('refreshInbox', 'true');
      } else {
        console.error('Failed to send email:', response.error);
        setErrorMessage('Failed to send email. Please try again.');
        setTimeout(() => setErrorMessage(''), 5000);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setErrorMessage('Error sending email. Please check your connection and try again.');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const renderTechnologyCard = (result: SearchResult, index: number) => {
    const metadata = result.metadata;
    
    // Skip if card was closed
    if (closedCards.has(index)) {
      return null;
    }
    
    // Parse researcher details - API now returns parsed objects, not JSON strings
    let researchers = [];
    try {
      // researcher_details is now an array (already parsed by API)
      const researcherDetails = Array.isArray(metadata.researcher_details) 
        ? metadata.researcher_details 
        : (typeof metadata.researcher_details === 'string' ? JSON.parse(metadata.researcher_details || '[]') : []);
      
      researchers = researcherDetails.map((r: any) => ({
        name: r.name || 'Unknown Researcher',
        url: r.url || undefined
      }));
      
      // If no researchers, fallback to tech manager details
      if (researchers.length === 0 || researchers[0].name === 'Unknown Researcher') {
        // Try tech_manager_details first (already parsed array by API)
        const techManagerDetails = Array.isArray(metadata.tech_manager_details)
          ? metadata.tech_manager_details
          : (typeof metadata.tech_manager_details === 'string' ? JSON.parse(metadata.tech_manager_details || '[]') : []);
        
        if (techManagerDetails && techManagerDetails.length > 0) {
          researchers = techManagerDetails.map((tm: any) => ({
            name: tm.name || 'Unknown',
            url: undefined
          }));
        } else {
          // Try tech_manager_default as fallback (already parsed object by API)
          const techManagerDefault = typeof metadata.tech_manager_default === 'object' && metadata.tech_manager_default !== null
            ? metadata.tech_manager_default
            : (typeof metadata.tech_manager_default === 'string' ? JSON.parse(metadata.tech_manager_default || '{}') : {});
          
          if (techManagerDefault && techManagerDefault.name) {
            researchers = [{
              name: techManagerDefault.name,
              url: undefined
            }];
          } else {
            // Final fallback to Unknown Researcher
            researchers = [{ name: 'Unknown Researcher' }];
          }
        }
      }
    } catch (e) {
      console.error('Error parsing researcher/tech manager details:', e);
      researchers = [{ name: 'Unknown Researcher' }];
    }

    let developmentStage = '';
    let trlDisplay = 'TRL Not Disclosed';
    try {
      // NEW FORMAT: Use metadata.calculated_trl directly (numeric value)
      let calculatedTrl: number | null = null;
      
      if (metadata.calculated_trl !== undefined && metadata.calculated_trl !== null) {
        // New format: calculated_trl is already numeric
        calculatedTrl = typeof metadata.calculated_trl === 'string' 
          ? parseInt(metadata.calculated_trl) 
          : metadata.calculated_trl;
      } else {
        // FALLBACK: Old format - parse development_stage JSON
        try {
          const devStage = JSON.parse(metadata.development_stage || '{}');
          const calculatedTrlRaw = devStage.calculated_trl;
          if (calculatedTrlRaw) {
            // Extract numeric value using regex
            const trlMatch = String(calculatedTrlRaw).match(/(\d+)/);
            calculatedTrl = trlMatch ? parseInt(trlMatch[1]) : null;
          }
        } catch (parseError) {
          console.warn('Could not parse development_stage:', parseError);
        }
      }
      
      // Check confidence_score - show TRL if:
      // 1. confidence_score is not available (undefined/null) - for older chats
      // 2. confidence_score exists and is >= 4
      // Otherwise show "Not Disclosed" only if confidence_score exists and is < 4
      const confidenceScore = metadata.confidence_score !== undefined && metadata.confidence_score !== null
        ? (typeof metadata.confidence_score === 'string' ? parseInt(metadata.confidence_score) : metadata.confidence_score)
        : null;
      
      if (calculatedTrl !== null && !isNaN(calculatedTrl)) {
        developmentStage = String(calculatedTrl);
        // If confidence_score is not available (null), show TRL (for older chats)
        if (confidenceScore === null) {
          trlDisplay = `TRL ${developmentStage}`;
        } 
        // If confidence_score exists and is >= 4, show TRL
        else if (confidenceScore >= 4) {
          trlDisplay = `TRL ${developmentStage}`;
        } 
        // If confidence_score exists and is < 4, normally show "Not Disclosed"
        // BUT: if development_stage.original_trl is not null, we still show the calculated TRL
        else {
          let hasOriginalTrl = false;
          try {
            if (metadata.development_stage) {
              const devStage = JSON.parse(metadata.development_stage || '{}');
              const originalTrl = devStage.original_trl ?? devStage.original_trl_input;
              if (originalTrl !== undefined && originalTrl !== null && String(originalTrl).trim() !== '') {
                hasOriginalTrl = true;
              }
            }
          } catch (originalTrlError) {
            console.warn('Could not parse development_stage for original_trl:', originalTrlError);
          }

          trlDisplay = hasOriginalTrl ? `TRL ${developmentStage}` : 'TRL Not Disclosed';
        }
      } else {
        trlDisplay = 'TRL Not Disclosed';
      }
    } catch (e) {
      console.warn('Error extracting TRL:', e);
      trlDisplay = 'TRL Not Disclosed';
    }

    // Create SavedItem for mobile card
    const cardId = `tech-${index}`;
    const savedItem: SavedItem = {
      id: cardId,
      type: SavedCardType.Tech,
      relevance: Math.round(result.percentage),
      title: metadata.utech_title || metadata.title || 'Unknown Technology',
      location: `${metadata.university_name || 'Unknown'}, ${metadata.university_country || 'Unknown'}`,
      detail: trlDisplay,
      verified: true,
      showStatusIcon: false
    };

    const techCardData = {
      relevanceScore: Math.round(result.percentage),
      title: metadata.utech_title || metadata.title,
      institution: metadata.university_name,
      location: metadata.university_country,
      trl: trlDisplay,
      trlReasoning: metadata.calculated_trl_statement, // TRL reasoning statement
      researchers: researchers,
      summary: metadata.utech_summary,
      ipSummary: metadata.ip_summary,
      publicationsSummary: metadata.publications_summary,
      activeTab: "Summary" as const,
      onContact: () => handleContactUniversity({
        techTitle: metadata.utech_title || metadata.title,
        techId: metadata.utech_technology_id || metadata.id.toString(), // Always use utech_technology_id for data fetching
        university: metadata.university_name || '', // for the Talk to Concierge lead
        referenceNumber: metadata.reference_number || '', // Separate reference number for display
        researcherName: researchers[0]?.name || 'Researcher',
        userName: userName,
        personalSummary: personalSummary,
        rdSummary: rdSummary
      }),
      onClose: closeModal,
      profileIncomplete: profileIncomplete, // Add profile completion status
      onBookmarkToggle: (isBookmarked: boolean) => handleBookmarkToggle(cardId),
      onLikeDislike: (liked: boolean | null) => handleLikeDislike(cardId, liked),
      initialBookmarked: bookmarkedCards.has(cardId),
      initialLiked: likedCards.get(cardId) || null
    };

    return (
      <div key={index} className="flex-shrink-0">
        {/* Mobile: Use existing SavedItemCard */}
        <div className="block sm:hidden">
          <SavedItemCard
            item={savedItem}
            isBookmarked={bookmarkedCards.has(cardId)}
            onBookmarkToggle={handleBookmarkToggle}
            onReadMore={() => handleReadMore(SavedCardType.Tech, techCardData, index)}
          />
        </div>
        
        {/* Desktop: Use full TechCard */}
        <div className="hidden sm:block">
          <TechCard
            contactLocked={!upgrade.ent.can_contact}
            onLockedContact={() => upgrade.gate(false, 'Tech Transfer Office contacts', undefined, 'can_contact')}
            relevanceScore={result.algorithm === 'sql_broad_query' ? 'Keyword match' : Math.round(result.percentage)}
            title={metadata.utech_title || metadata.title}
            institution={metadata.university_name}
            location={metadata.university_country}
            trl={trlDisplay}
            trlReasoning={metadata.calculated_trl_statement}
            researchers={researchers}
            summary={metadata.utech_summary}
            ipSummary={metadata.ip_summary}
            publicationsSummary={metadata.publications_summary}
            activeTab="Summary"
            onContact={() => handleContactUniversity({
              techTitle: metadata.utech_title || metadata.title,
              techId: metadata.utech_technology_id || metadata.id.toString(), // Always use utech_technology_id for data fetching
              university: metadata.university_name || '', // for the Talk to Concierge lead
              referenceNumber: metadata.reference_number || '', // Separate reference number for display
              researcherName: researchers[0]?.name || 'Researcher',
              userName: userName,
              personalSummary: personalSummary,
              rdSummary: rdSummary
            })}
            onClose={() => closeCard(index)}
            profileIncomplete={profileIncomplete}
            onBookmarkToggle={(isBookmarked) => handleBookmarkToggle(cardId)}
            onLikeDislike={(liked) => handleLikeDislike(cardId, liked)}
            initialBookmarked={bookmarkedCards.has(cardId)}
            initialLiked={likedCards.get(cardId) || null}
          />
        </div>
      </div>
    );
  };

  const renderMiniTechnologyCard = (result: SearchResult, index: number, developmentStage: string) => {
    const metadata = result.metadata;
    
    // Calculate TRL display based on confidence_score (before AI rescoring)
    let trlDisplay = 'TRL Not Disclosed';
    try {
      // Check confidence_score - show TRL if:
      // 1. confidence_score is not available (undefined/null) - for older chats
      // 2. confidence_score exists and is >= 4
      // Otherwise show "Not Disclosed" only if confidence_score exists and is < 4
      const confidenceScore = metadata.confidence_score !== undefined && metadata.confidence_score !== null
        ? (typeof metadata.confidence_score === 'string' ? parseInt(metadata.confidence_score) : metadata.confidence_score)
        : null;
      
      if (developmentStage !== 'Unknown') {
        // If confidence_score is not available (null), show TRL (for older chats)
        if (confidenceScore === null) {
          trlDisplay = `TRL ${developmentStage}`;
        } 
        // If confidence_score exists and is >= 4, show TRL
        else if (confidenceScore >= 4) {
          trlDisplay = `TRL ${developmentStage}`;
        } 
        // If confidence_score exists and is < 4, show "Not Disclosed"
        else {
          trlDisplay = 'TRL Not Disclosed';
        }
      } else {
        trlDisplay = 'TRL Not Disclosed';
      }
    } catch (e) {
      trlDisplay = 'TRL Not Disclosed';
    }
    
    return (
      <div className="w-full max-w-sm mx-auto bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 shadow-sm border border-orange-200 relative utech-nocopy">
        {/* Close button */}
        <button
          onClick={() => closeCard(index)}
          className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/80 flex items-center justify-center hover:bg-white transition-colors"
        >
          <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Relevance score */}
        <div className="text-xs font-medium text-orange-700 mb-2">
          {Math.round(result.percentage)}% relevance score
        </div>

        {/* Title */}
        <h3 className="font-semibold text-gray-900 text-sm mb-2 pr-8 leading-tight">
          {metadata.utech_title || metadata.title}
        </h3>

        {/* Institution and location */}
        <div className="flex items-center text-xs text-gray-600 mb-2">
          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {metadata.university_name}, {metadata.university_country}
        </div>

        {/* TRL */}
        <div className="text-xs text-gray-700 font-medium mb-3">
          {trlDisplay}
        </div>

        {/* Read More button and bookmark */}
        <div className="flex items-center justify-between">
          <button 
            onClick={onContact}
            className="flex-1 mr-2 px-4 py-2 bg-white/80 hover:bg-white text-gray-700 text-xs font-medium rounded-lg border border-gray-200 transition-colors"
          >
            Read More
          </button>
          <button className="w-8 h-8 flex items-center justify-center bg-white/80 hover:bg-white rounded-lg border border-gray-200 transition-colors">
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  const renderResearcherCard = (result: SearchResult, index: number) => {
    const metadata = result.metadata;
    
    // Skip if card was closed
    if (closedCards.has(index)) {
      return null;
    }
    
    // Based on the provided sample data structure for researchers
    const researcherName = metadata.researcher_name || 'Unknown Researcher';
    const techTitle = metadata.utech_title || metadata.title || 'Research Area';
    
    // Create tech array - for now single tech, but can be extended
    const techs = [
      {
        id: metadata.utech_technology_id || `tech-${index}`,
        title: techTitle
      }
    ];

    // Either may be missing (an Explorer is not sent the university).
    const institution = metadata.university_name || '';
    const location = metadata.university_country || '';

    // Create SavedItem for mobile card
    const cardId = `researcher-${index}`;
    const savedItem: SavedItem = {
      id: cardId,
      type: SavedCardType.Researcher,
      relevance: Math.round(result.percentage),
      title: researcherName,
      location: [institution, location].filter(Boolean).join(', '),
      detail: `${techs.length} research area${techs.length !== 1 ? 's' : ''}`,
      verified: true,
      showStatusIcon: false
    };

    const researcherCardData = {
      relevanceScore: Math.round(result.percentage),
      title: researcherName,
      institution: institution,
      location: location,
      techs: techs,
      onTechCardClick: handleTechCardClick,
      onClose: closeModal,
      onBookmarkToggle: (isBookmarked: boolean) => handleBookmarkToggle(cardId),
      onLikeDislike: (liked: boolean | null) => handleLikeDislike(cardId, liked),
      initialBookmarked: bookmarkedCards.has(cardId),
      initialLiked: likedCards.get(cardId) || null
    };

    return (
      <div key={index} className="flex-shrink-0">
        {/* Mobile: Use existing SavedItemCard */}
        <div className="block sm:hidden">
          <SavedItemCard
            item={savedItem}
            isBookmarked={bookmarkedCards.has(cardId)}
            onBookmarkToggle={handleBookmarkToggle}
            onReadMore={() => handleReadMore(SavedCardType.Researcher, researcherCardData, index)}
          />
        </div>
        
        {/* Desktop: Use full ResearcherCard */}
        <div className="hidden sm:block">
          <ResearcherCard
            relevanceScore={result.algorithm === 'sql_broad_query' ? 'Keyword match' : Math.round(result.percentage)}
            title={researcherName}
            institution={institution}
            location={location}
            techs={techs}
            onTechCardClick={handleTechCardClick}
            onClose={() => closeCard(index)}
            onBookmarkToggle={(isBookmarked) => handleBookmarkToggle(cardId)}
            onLikeDislike={(liked) => handleLikeDislike(cardId, liked)}
            initialBookmarked={bookmarkedCards.has(cardId)}
            initialLiked={likedCards.get(cardId) || null}
          />
        </div>
      </div>
    );
  };



  if (!searchResults || searchResults.length === 0) {
    return null;
  }

  // Filter out closed cards for count (using sorted results)
  const visibleResults = sortedSearchResults.filter((_, index) => !closedCards.has(index));
  
  if (visibleResults.length === 0) {
    return null;
  }

  // Determine which results to display based on showAllResults state
  const displayedResults = showAllResults 
    ? visibleResults 
    : visibleResults.slice(0, initialDisplayCount);
  
  const hasMoreResults = visibleResults.length > initialDisplayCount;
  const hiddenResultsCount = visibleResults.length - initialDisplayCount;

  // Show loading state while fetching saved states
  if (!isStateLoaded) {
    return (
      <div className="mt-3">
        <div className="text-xs font-medium text-grayscale-900 mb-3">
          Loading saved states...
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mt-3">
        <div className="text-xs font-medium text-grayscale-900 mb-3">
          {broadQueryMeta?.is_broad_query ? (
            <>
              Found {broadQueryMeta.total_results_count.toLocaleString()} result{broadQueryMeta.total_results_count !== 1 ? 's' : ''}
              {broadQueryMeta.total_results_count > broadQueryMeta.returned_results_count && (
                <span> (showing first {broadQueryMeta.returned_results_count})</span>
              )}
            </>
          ) : (
            <>
              Found {visibleResults.length} result{visibleResults.length !== 1 ? 's' : ''} (sorted by relevance)
              {!showAllResults && hasMoreResults && (
                <span className="text-grayscale-700"> • Showing top {initialDisplayCount}</span>
              )}
            </>
          )}
        </div>
        
        {/* Mobile: Single column layout for mini cards */}
        <div className="block sm:hidden space-y-4">
          {displayedResults.map((result, index) => {
            if (result.source_type === 'technology') {
              return renderTechnologyCard(result, index);
            } else if (result.source_type === 'researcher') {
              return renderResearcherCard(result, index);
            }
            return null;
          }).filter(Boolean)}
        </div>

        {/* Desktop: Flexible wrapping layout for full cards */}
        <div className="hidden sm:flex flex-wrap gap-6 justify-start">
          {displayedResults.map((result, index) => {
            if (result.source_type === 'technology') {
              return renderTechnologyCard(result, index);
            } else if (result.source_type === 'researcher') {
              return renderResearcherCard(result, index);
            }
            return null;
          }).filter(Boolean)}
        </div>

        {/* Show More/Show Less Button */}
        {hasMoreResults && (
          <div className="mt-4 flex justify-center">
            <button
              onClick={() => { trackClick('search_click_show_more'); setShowAllResults(!showAllResults); }}
              className="flex items-center gap-2 px-4 py-2 bg-primary-100 hover:bg-primary-200 text-primary-og-600 font-grotesk text-sm font-medium rounded-full transition-colors border border-primary-300"
            >
              {showAllResults ? (
                <>
                  <span>Show Less</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </>
              ) : (
                <>
                  <span>Show {hiddenResultsCount} More Result{hiddenResultsCount !== 1 ? 's' : ''}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </>
              )}
            </button>
          </div>
        )}

        {/* Narrow Down Prompt - Show for broad query results */}
        {broadQueryMeta?.is_broad_query && onSendMessage && showExtensiveSearchPrompt && isFreshMessage && (
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-grayscale-900 font-grotesk mb-3">
              Would you like to narrow down these results for more accurate, relevant matches?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setHasInteractedWithPrompt(true);
                  setShowExtensiveSearchPrompt(false);
                  onSendMessage('Yes');
                }}
                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-grotesk text-sm font-medium rounded-lg transition-colors"
              >
                Yes, narrow down
              </button>
              <button
                onClick={() => {
                  setHasInteractedWithPrompt(true);
                  setShowExtensiveSearchPrompt(false);
                  onSendMessage('No');
                }}
                className="px-4 py-2 bg-white hover:bg-gray-50 text-grayscale-900 font-grotesk text-sm font-medium rounded-lg transition-colors border border-gray-300"
              >
                No, these are fine
              </button>
            </div>
          </div>
        )}

        {/* Extensive Search Prompt - Show only for non-broad queries */}
        {!broadQueryMeta?.is_broad_query && !extensiveSearch && onRunExtensiveSearch && showExtensiveSearchPrompt && isFreshMessage && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-grayscale-900 font-grotesk mb-3">
              Want more comprehensive results? Run an extensive search to explore all available technologies.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setHasInteractedWithPrompt(true);
                  setShowExtensiveSearchPrompt(false);
                  if (onRunExtensiveSearch) {
                    onRunExtensiveSearch();
                  }
                }}
                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-grotesk text-sm font-medium rounded-lg transition-colors"
              >
                Yes, run extensive search
              </button>
              <button
                onClick={() => {
                  setHasInteractedWithPrompt(true);
                  setShowExtensiveSearchPrompt(false);
                }}
                className="px-4 py-2 bg-white hover:bg-gray-50 text-grayscale-900 font-grotesk text-sm font-medium rounded-lg transition-colors border border-gray-300"
              >
                No, thanks
              </button>
            </div>
          </div>
        )}

        {/* Chat Feedback Component */}
        {sessionId && (
          <ChatFeedback
            sessionId={sessionId}
            messageId={aiMessageId}
            onSubmitted={() => {
              // Optional: Could trigger some UI update after feedback submission
              console.log('Feedback submitted successfully');
            }}
          />
        )}
      </div>

      {/* Modal for mobile card details */}
      {modalOpen && (
        <div 
          className="fixed inset-0 w-full h-full flex justify-center items-center bg-black/70 backdrop-blur-sm z-50 p-4"
          onClick={closeModal}
        >
          <div 
            className="rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-auto"
            onClick={e => e.stopPropagation()}
          >
            {modalOpen.type === 'tech' ? (
              <TechCard 
                            contactLocked={!upgrade.ent.can_contact}
                            onLockedContact={() => upgrade.gate(false, 'Tech Transfer Office contacts', undefined, 'can_contact')}
                {...modalOpen.data}
                onContact={modalOpen.data.onContact}
                profileIncomplete={profileIncomplete}
                onBookmarkToggle={modalOpen.data.onBookmarkToggle}
                onLikeDislike={modalOpen.data.onLikeDislike}
                initialBookmarked={modalOpen.data.initialBookmarked}
                initialLiked={modalOpen.data.initialLiked}
              />
            ) : (
              <ResearcherCard 
                {...modalOpen.data}
                onTechCardClick={handleTechCardClick}
                onBookmarkToggle={modalOpen.data.onBookmarkToggle}
                onLikeDislike={modalOpen.data.onLikeDislike}
                initialBookmarked={modalOpen.data.initialBookmarked}
                initialLiked={modalOpen.data.initialLiked}
              />
            )}
          </div>
        </div>
      )}

      {/* Contact University Modal (kept mounted so edits persist across preview) */}
      <ContactUniversityModal
        onTalkToConcierge={onTalkToConcierge}
        surface="chat"
        university={currentContactData?.university || ''}
        isOpen={!!contactModalOpen && !!currentContactData}
        onClose={closeContactModal}
        onPreview={handlePreviewMessage}
        techTitle={currentContactData?.techTitle || ''}
        techId={currentContactData?.techId || ''}
        referenceNumber={currentContactData?.referenceNumber}
        researcherName={currentContactData?.researcherName || ''}
        userName={currentContactData?.userName || ''}
        personalSummary={currentContactData?.personalSummary || ''}
        rdSummary={currentContactData?.rdSummary || ''}
      />

      {/* Preview Message Modal */}
      <PreviewMessageModal
        isOpen={!!previewModalOpen && !!previewData && !!currentContactData}
        onClose={closePreviewModal}
        onEdit={() => {
          setPreviewModalOpen(false);
          setContactModalOpen(true);
        }}
        onSend={handleSendEmail}
        message={previewData?.message || ''}
        summary={
          previewData && currentContactData
            ? (previewData.summaryType === 'personal'
                ? currentContactData.personalSummary
                : currentContactData.rdSummary)
            : ''
        }
        techTitle={currentContactData?.techTitle || ''}
        techId={currentContactData?.techId || ''}
        referenceNumber={currentContactData?.referenceNumber || ''} // Add reference number for display
        techManagerName={previewData?.techManagerName || ''} // Use tech manager name from preview data
        userName={currentContactData?.userName || ''}
      />

      {/* Success/Error Notifications */}
      <FloatingToast variant="success" message={successMessage} visible={!!successMessage} />
      <FloatingToast variant="error" message={errorMessage} visible={!!errorMessage} />
    </>
  );
};
